import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000';
// ─── Agency / Admin axios instance ───────────────────────────────────────────
const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});
export const getFileUrl = (path) => {
  if (!path) return null;
  // Si c'est déjà une URL complète, la retourner
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // Si c'est un chemin relatif, ajouter le base URL
  if (path.startsWith('/uploads/')) {
    return `${STORAGE_URL}${path}`;
  }
  return `${STORAGE_URL}/uploads/${path}`;
};
// ─── Intercepteur pour corriger les URLs dans les réponses ────────────────
api.interceptors.response.use(
  (response) => {
    // Si la réponse contient des données avec des URLs d'images/vidéos
    const data = response.data;
    
    // Fonction récursive pour corriger les URLs
    const fixUrls = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      
      // Ne modifier QUE les tableaux et les objets bruts (provenant du JSON)
      // On ignore les Blob, ArrayBuffer, File, Date, etc.
      if (obj.constructor !== Object && !Array.isArray(obj)) {
        return obj;
      }
      
      // Si c'est un tableau, traiter chaque élément
      if (Array.isArray(obj)) {
        return obj.map(item => fixUrls(item));
      }
      
      // Si c'est un objet, traiter chaque propriété
      const result = { ...obj };
      for (const key of Object.keys(result)) {
        const value = result[key];
        
        // Si la valeur est une URL d'upload
        if (typeof value === 'string' && value.startsWith('/uploads/')) {
          result[key] = getFileUrl(value);
        }
        
        // Si la propriété est un tableau d'objets (images, videos, etc.)
        if (key === 'images' || key === 'videos' || key === 'documents') {
          if (Array.isArray(value)) {
            result[key] = value.map(item => fixUrls(item));
          }
        }
        
        // Récursion pour les objets imbriqués
        if (typeof value === 'object' && value !== null) {
          result[key] = fixUrls(value);
        }
      }
      return result;
    };
    
    // Corriger les URLs dans la réponse
    response.data = fixUrls(data);
    return response;
  },
  async (error) => {
    // ... garder la gestion d'erreur existante
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const { data } = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ─── Portal (Client/Locataire) axios instance ────────────────────────────────
const portalAxios = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

portalAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('portalAccessToken') || localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

portalAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('portalRefreshToken') || localStorage.getItem('refreshToken');
        const { data } = await axios.post(`${API_URL}/portal/auth/refresh-token`, { refreshToken });
        localStorage.setItem('portalAccessToken', data.accessToken);
        localStorage.setItem('portalRefreshToken', data.refreshToken);
        localStorage.setItem('accessToken', data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        return portalAxios(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('portalAccessToken');
        localStorage.removeItem('portalRefreshToken');
        localStorage.removeItem('portalUser');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/portal/login';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);



export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const agencyAPI = {
  get: () => api.get('/agency'),
  update: (data) => api.put('/agency', data),
  getStats: () => api.get('/agency/stats'),
  getUsers: (params) => api.get('/agency/users', { params }),
  createUser: (data) => api.post('/agency/users', data),
  updateUser: (id, data) => api.put(`/agency/users/${id}`, data),
  deleteUser: (id) => api.delete(`/agency/users/${id}`),
  getRoles: () => api.get('/agency/roles'),
  getAgency: () => api.get('/agency'),  
  updateAgency: (data) => api.put('/agency', data), 
};
// À ajouter avant `export default api;`  
  
export const portalAPI = {  
  auth: {  
    login: (data) => portalAxios.post('/portal/auth/login', data),  
    refreshToken: (data) => portalAxios.post('/portal/auth/refresh-token', data),  
    logout: () => portalAxios.post('/portal/auth/logout'),  
    me: () => portalAxios.get('/portal/auth/me'),  
    updateMe: (data) => portalAxios.put('/portal/auth/me', data),  
  },  
  
  billing: {
    // Factures
    listInvoices: (params) => portalAxios.get('/portal/billing/invoices', { params }),
    getInvoiceById: (id) => portalAxios.get(`/portal/billing/invoices/${id}`),
    
    // Contrats de location
    listLeaseContracts: (params) => portalAxios.get('/portal/billing/lease-contracts', { params }),
    getLeaseContractById: (id) => portalAxios.get(`/portal/billing/lease-contracts/${id}`),
    getLeaseContractPaymentSchedule: (id) => portalAxios.get(`/portal/billing/lease-contracts/${id}/payment-schedule`),
    
    // Échéances loyer
    listLeasePayments: (params) => portalAxios.get('/portal/billing/lease-payments', { params }),
    getLeasePaymentById: (id) => portalAxios.get(`/portal/billing/lease-payments/${id}`),
     payInvoice: (id, formData) => portalAxios.post(`/portal/billing/invoices/${id}/pay`, formData),
    // Paiement échéance (automatique : OM, Wave, etc.)
    checkoutLeasePayment: (id, formData) => portalAxios.post(`/portal/billing/lease-payments/${id}/checkout`, formData),
    
    // Paiement échéance (manuel : virement, chèque, espèces)
    submitManualPayment: (id, formData) => portalAxios.post(`/portal/billing/lease-payments/${id}/manual-payment`, formData),
    
    // Paiement facture (manuel)
    submitManualInvoicePayment: (id, formData) => portalAxios.post(`/portal/billing/invoices/${id}/pay-manual`, formData),
    
    // Historique paiements
    listPayments: (params) => portalAxios.get('/portal/billing/payments', { params }),
    listPaymentIntents: (params) => portalAxios.get('/portal/payments/my', { params }),
    
    // Méthodes de paiement de l'agence
    getAgencyPaymentMethods: () => portalAxios.get('/portal/billing/payment-methods'),
  },  

  payments: {
    listMyIntents: (params) => portalAxios.get('/portal/payments/my', { params }),
    getIntentById: (id) => portalAxios.get(`/portal/payments/${id}`),
    cancelIntent: (id) => portalAxios.delete(`/portal/payments/${id}`),
    checkoutLeasePayment: (id, data) => portalAxios.post(`/portal/billing/lease-payments/${id}/checkout`, data,),
    listPayments: (params) => portalAxios.get('/portal/billing/payments', { params }),
       createLeaseIntent: (payload) => portalAxios.post('/portal/payments/intents', payload),
    
    // Créer intent pour facture
    createInvoiceIntent: (payload) => portalAxios.post('/payments/invoice', payload)
    //createInvoiceIntent: (payload) => portalAxios.post('/portal/payments/intents/invoice', payload),
  },
  client: {
    listAnnouncements: (params) => portalAxios.get('/portal/client/announcements', { params }),
    getAnnouncementById: (id) => portalAxios.get(`/portal/client/announcements/${id}`),
    listAppointments: (params) => portalAxios.get('/portal/client/appointments', { params }),
    createAppointment: (data) => portalAxios.post('/portal/client/appointments', data),
    listFavorites: (params) => portalAxios.get('/portal/client/favorites', { params }),
    addFavorite: (data) => portalAxios.post('/portal/client/favorites', data),
    removeFavorite: (id) => portalAxios.delete(`/portal/client/favorites/${id}`),
  },

  maintenance: {
    listTickets: (params) => portalAxios.get('/portal/maintenance/tickets', { params }),
    getTicketById: (id) => portalAxios.get(`/portal/maintenance/tickets/${id}`),
    createTicket: (data) => portalAxios.post('/portal/maintenance/tickets', data),
    addComment: (id, data) => portalAxios.post(`/portal/maintenance/tickets/${id}/comments`, data),
    getCategories: () => portalAxios.get('/portal/maintenance/categories'),
    getMyProperties: () => portalAxios.get('/portal/maintenance/my-properties'),
    // Pièces jointes
    uploadAttachment: (id, formData) => portalAxios.post(`/portal/maintenance/tickets/${id}/attachments`, formData),
    deleteAttachment: (ticketId, attachmentId) => portalAxios.delete(`/portal/maintenance/tickets/${ticketId}/attachments/${attachmentId}`),
  },
  
  notifications: {  
    list: (params) => portalAxios.get('/portal/notifications', { params }),  
    markAsRead: (id) => portalAxios.put(`/portal/notifications/${id}/read`),  
    markAllAsRead: () => portalAxios.put('/portal/notifications/read-all'),  
  },  
  
  getPaymentMethods: () => portalAxios.get('/payment-methods'),  
  
  webhooks: {  
    handle: (provider, data) => axios.post(`${API_URL}/portal/webhooks/${provider}`, data),  
  },  
};  
export const paymentIntentAPI = {
  listAgencyIntents: (params) => api.get('/payment-intents/agency', { params }),
  getById: (id) => api.get(`/payment-intents/${id}`),
  validate: (id, data) => api.put(`/payment-intents/${id}/validate`, data),
};

export const paymentAPI = {
  listAgencyPayments: (params) => api.get('/payments', { params }),
  verifyPayment: (id, data) => api.put(`/payments/${id}/verify`, data),
};

// ─── Agency Payment Methods API ──────────────────────────────────────────────
export const agencyPaymentMethodAPI = {
  list: () => api.get('/agency-payment-methods'),
  listAll: () => api.get('/payment-methods', { params: { active_only: false } }),
  update: (methods) => api.put('/agency-payment-methods', { methods }),
};

// Helper function to get payment type label in French
export const getPaymentTypeLabel = (paymentType) => {
  switch (paymentType) {
    case 'tranche':
      return 'Paiement par tranche';
    case 'avance':
      return 'Paiement par avance';
    case 'total':
    default:
      return 'Paiement total';
  }
};

// Helper function to get payment status label in French
export const getPaymentStatusLabel = (status) => {
  switch (status) {
    case 'pending':
      return 'En attente';
    case 'partial':
      return 'Partiellement payé';
    case 'paid':
      return 'Payé';
    case 'rejected':
      return 'Rejeté';
    case 'cancelled':
      return 'Annulé';
    default:
      return status;
  }
};

export const propertyAPI = {
  list: (params) => api.get('/properties', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  uploadImages: (id, formData) => api.post(`/properties/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeImage: (id, imageId) => api.delete(`/properties/${id}/images/${imageId}`),
  addFeature: (id, data) => api.post(`/properties/${id}/features`, data),
  addOwner: (id, data) => api.post(`/properties/${id}/owners`, data),
  getTypes: () => api.get('/properties/types'),
  getStatuses: () => api.get('/properties/statuses'),
  getFeatures: () => api.get('/properties/features'),
  uploadVideos: (id, formData) => api.post(`/properties/${id}/videos/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),  
 addVideoUrl: (id, data) => api.post(`/properties/${id}/videos/url`, data),  
 removeVideo: (id, videoId) => api.delete(`/properties/${id}/videos/${videoId}`),
    
 removeOwner: (id, ownerId) => api.delete(`/properties/${id}/owners/${ownerId}`),
};

export const propertyUnitAPI = {
  listByProperty: (propertyId) => api.get(`/properties/${propertyId}/units`),
  getById: (id) => api.get(`/property-units/${id}`),
  create: (propertyId, data) => api.post(`/properties/${propertyId}/units`, data),
  update: (id, data) => api.put(`/property-units/${id}`, data),
  delete: (id) => api.delete(`/property-units/${id}`),
  uploadImages: (id, formData) => api.post(`/property-units/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  removeImage: (id, imageId) => api.delete(`/property-units/${id}/images/${imageId}`),
  uploadVideos: (id, formData) => api.post(`/property-units/${id}/videos/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  addVideoUrl: (id, data) => api.post(`/property-units/${id}/videos/url`, data),
  removeVideo: (id, videoId) => api.delete(`/property-units/${id}/videos/${videoId}`),
};

export const leaseAPI = {
  list: (params) => api.get('/lease-contracts', { params }),
  getById: (id) => api.get(`/lease-contracts/${id}`),
  create: (data) => api.post('/lease-contracts', data),
  update: (id, data) => api.put(`/lease-contracts/${id}`, data),
  delete: (id) => api.delete(`/lease-contracts/${id}`),
  terminate: (id, data) => api.post(`/lease-contracts/${id}/terminate`, data),
  addPayment: (id, data) => api.post(`/lease-contracts/${id}/payments`, data),
  getPaymentSchedule: (id) => api.get(`/lease-contracts/${id}/payment-schedule`),
  getOverduePayments: () => api.get('/lease-contracts/overdue-payments'),
  validate: (id) => api.post(`/lease-contracts/${id}/validate`),  
 sign: (id) => api.post(`/lease-contracts/${id}/sign`),  
 activate: (id) => api.post(`/lease-contracts/${id}/activate`),  
 archive: (id) => api.post(`/lease-contracts/${id}/archive`),  
 getAllPayments: (params) => api.get('/lease-contracts/payments/all', { params }),  
 markPaymentPaid: (paymentId, data) => api.put(`/lease-contracts/payments/${paymentId}/mark-paid`, data),  
  sendRelance: (paymentId, data) => api.post(`/lease-contracts/payments/${paymentId}/relance`, data),
  sendPaymentSMS: (paymentId, data) => api.post(`/lease-contracts/payments/${paymentId}/sms`, data),
  notifyOverduePayment: (paymentId, data) => api.post(`/lease-contracts/overdue-payments/${paymentId}/notify`, data),
  notifyAllOverduePayments: (data) => api.post('/lease-contracts/overdue-payments/notify-all', data),
  downloadRentReceipt: (paymentId) => api.get(`/lease-contracts/payments/${paymentId}/receipt/pdf`, { responseType: 'blob' }),
};

export const invoiceAPI = {
  list: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  send: (id) => api.post(`/invoices/${id}/send`),
  markPaid: (id) => api.post(`/invoices/${id}/mark-paid`),
  addPayment: (id, data) => api.post(`/invoices/${id}/payments`, data),
  getPaymentMethods: () => api.get('/invoices/payment-methods'),
};

export const clientAPI = {
  list: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  createWithAccount: (data) => api.post('/clients/with-account', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  addInteraction: (id, data) => api.post(`/clients/${id}/interactions`, data),
  addAppointment: (id, data) => api.post(`/clients/${id}/appointments`, data),
};
export const mandateAPI = {  
  list: (params) => api.get('/mandates', { params }),  
  getStats: () => api.get('/mandates/stats'),
  getById: (id) => api.get(`/mandates/${id}`),  
  create: (data) => api.post('/mandates', data),  
  update: (id, data) => api.put(`/mandates/${id}`, data),  
  delete: (id) => api.delete(`/mandates/${id}`),  
  generatePdf: (id, data) => api.post(`/mandates/${id}/generate-pdf`, data),
  signClient: (id, data) => api.post(`/mandates/${id}/sign-client`, data),
  signAgency: (id, data) => api.post(`/mandates/${id}/sign-agency`, data),
  activate: (id) => api.post(`/mandates/${id}/activate`),
  terminate: (id, data) => api.post(`/mandates/${id}/terminate`, data),
};  
   
export const reminderAPI = {  
  list: (params) => api.get('/reminders', { params }),  
  create: (data) => api.post('/reminders', data),  
  markDone: (id) => api.put(`/reminders/${id}/done`),  
  delete: (id) => api.delete(`/reminders/${id}`),  
};
export const leadAPI = {
  list: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  convert: (id) => api.post(`/leads/${id}/convert`),
  getPipeline: () => api.get('/leads/pipeline'),
  getInteractions: (id) => api.get(`/leads/${id}/interactions`),
  addInteraction: (id, data) => api.post(`/leads/${id}/interactions`, data),
};

export const tenantAPI = {
  list: (params) => api.get('/tenants', { params }),
  getById: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  createWithAccount: (data) => api.post('/tenants/with-account', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
  checkSolvability: (id) => api.get(`/tenants/${id}/solvability`),
};

export const maintenanceAPI = {
  list: (params) => api.get('/maintenance', { params }),
  getById: (id) => api.get(`/maintenance/${id}`),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
  delete: (id) => api.delete(`/maintenance/${id}`),
  updateStatus: (id, data) => api.put(`/maintenance/${id}/status`, data),
  assign: (id, data) => api.post(`/maintenance/${id}/assign`, data),
  addComment: (id, data) => api.post(`/maintenance/${id}/comments`, data),
  getCategories: () => api.get('/maintenance/categories'),
  listProviders: (params) => api.get('/maintenance/providers', { params }),
  createProvider: (data) => api.post('/maintenance/providers', data),
  updateProvider: (id, data) => api.put(`/maintenance/providers/${id}`, data),
  deleteProvider: (id) => api.delete(`/maintenance/providers/${id}`),
  // Pièces jointes
  uploadAttachment: (id, formData) => api.post(`/maintenance/${id}/attachments`, formData),
  deleteAttachment: (ticketId, attachmentId) => api.delete(`/maintenance/${ticketId}/attachments/${attachmentId}`),
};

export const announcementAPI = {
  list: (params) => api.get('/announcements', { params }),
  search: (params) => api.get('/announcements/search', { params }),
  getById: (id) => api.get(`/announcements/${id}`),
  getBySlug: (slug) => api.get(`/announcements/slug/${slug}`),
  create: (data) => api.post('/announcements', data),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  delete: (id) => api.delete(`/announcements/${id}`),
  publish: (id) => api.post(`/announcements/${id}/publish`),
  addFavorite: (id) => api.post(`/announcements/${id}/favorite`),
  removeFavorite: (id) => api.delete(`/announcements/${id}/favorite`),
  getFavorites: () => api.get('/announcements/favorites'),
  createAlert: (data) => api.post('/announcements/alerts', data),
  getAlerts: () => api.get('/announcements/alerts'),
  deleteAlert: (alertId) => api.delete(`/announcements/alerts/${alertId}`),
  addContact: (id, data) => api.post(`/announcements/${id}/contact`, data),
  getStats: (id) => api.get(`/announcements/${id}/stats`),
  incrementShares: (id) => api.post(`/announcements/${id}/share`),
  getRecommendations: (id) => api.get(`/announcements/${id}/recommendations`),
  getPersonalizedRecommendations: () => api.get('/announcements/recommendations'),
};
// ─── Chatbot API (public — pas besoin de token) ─────────────────────────────
export const chatbotAPI = {
  chat: (data) => axios.post(`${API_URL}/chatbot/message`, data),
  getHistory: (sessionId) => axios.get(`${API_URL}/chatbot/history/${sessionId}`),
  getHealth: () => axios.get(`${API_URL}/chatbot/health`),
};
export const notificationAPI = {
  list: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  create: (data) => api.post('/notifications', data),
  getTemplates: () => api.get('/notifications/templates'),
  getVapidKey: () => api.get('/notifications/vapid-public-key'),  
  pushSubscribe: (sub) => api.post('/notifications/push-subscribe', sub),  
  pushUnsubscribe: (endpoint) => api.delete('/notifications/push-unsubscribe', { data: { endpoint } }),
};

export const documentAPI = {
  list: (params) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  create: (formData) => api.post('/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  addVersion: (id, formData) => api.post(`/documents/${id}/versions`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/documents/${id}`),
};

export const reportAPI = {
  getDashboard: () => api.get('/reports/dashboard'),
  getRevenueChart: (params) => api.get('/reports/revenue-chart', { params }),
  getOccupancyRate: () => api.get('/reports/occupancy-rate'),
  getAuditLogs: (params) => api.get('/reports/audit-logs', { params }),
  getActivityLogs: (params) => api.get('/reports/activity-logs', { params }),
  getTransactions: (params) => api.get('/reports/transactions', { params }),
  getTransactionSummary: (params) => api.get('/reports/transaction-summary', { params }),
  // Rapports financiers par mandat/propriétaire
  getMandateFinancial: (mandateId) => api.get(`/reports/mandate-financial/${mandateId}`),
  getOwnerFinancial: (clientId) => api.get(`/reports/owner-financial/${clientId}`),
  getAgencyMandatesFinancial: (params) => api.get('/reports/agency-mandates-financial', { params }),
};

export const pdfAPI = {
  downloadInvoice: (id) => api.get(`/pdf/invoice/${id}`, { responseType: 'blob' }),
  downloadContract: (id) => api.get(`/pdf/contract/${id}`, { responseType: 'blob' }),
  downloadMandate: (id) => api.get(`/pdf/mandate/${id}`, { responseType: 'blob' }),
};
export const publicAPI = {  
  getAnnouncements: (params) => api.get('/announcements/public', { params }),  
  sendContact: (id, data) => api.post(`/announcements/${id}/contact`, data),  
   getPublicNotifications: (params) => api.get('/announcements/notifications/public', { params }),
};

export const inventoryAPI = {
  list: (params) => api.get('/inventories', { params }),
  getById: (id) => api.get(`/inventories/${id}`),
  create: (data) => api.post('/inventories', data),
  update: (id, data) => api.put(`/inventories/${id}`, data),
  delete: (id) => api.delete(`/inventories/${id}`),
  updateItems: (id, data) => api.post(`/inventories/${id}/items`, data),
  downloadPDF: (id) => api.get(`/inventories/${id}/pdf`, { responseType: 'blob' }),
  compare: (leaseId) => api.get(`/inventories/compare/${leaseId}`),
  downloadComparisonPDF: (leaseId) => api.get(`/inventories/compare/${leaseId}/pdf`, { responseType: 'blob' }),
};

export const saleAPI = {
  list: (params) => api.get('/sales', { params }),
  getById: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  getStats: () => api.get('/sales/stats'),
  downloadCompromisPDF: (id) => api.get(`/sales/${id}/pdf`, { responseType: 'blob' }),
  addOffer: (saleId, data) => api.post(`/sales/${saleId}/offers`, data),
  updateOfferStatus: (saleId, offerId, data) => api.put(`/sales/${saleId}/offers/${offerId}`, data),
  deleteOffer: (saleId, offerId) => api.delete(`/sales/${saleId}/offers/${offerId}`),
  addDocument: (saleId, data) => api.post(`/sales/${saleId}/documents`, data),
  deleteDocument: (saleId, docId) => api.delete(`/sales/${saleId}/documents/${docId}`),
};

export const litigationAPI = {
  list: (params) => api.get('/litigations', { params }),
  getById: (id) => api.get(`/litigations/${id}`),
  create: (data) => api.post('/litigations', data),
  update: (id, data) => api.put(`/litigations/${id}`, data),
  delete: (id) => api.delete(`/litigations/${id}`),
  getStats: () => api.get('/litigations/stats'),
  downloadFormalNoticePDF: (id) => api.get(`/litigations/${id}/formal-notice-pdf`, { responseType: 'blob' }),
  addEvent: (litigationId, data) => api.post(`/litigations/${litigationId}/events`, data),
  deleteEvent: (litigationId, eventId) => api.delete(`/litigations/${litigationId}/events/${eventId}`),
  savePaymentSchedules: (litigationId, data) => api.post(`/litigations/${litigationId}/schedules`, data),
  recordSchedulePayment: (litigationId, scheduleId, data) => api.post(`/litigations/${litigationId}/schedules/${scheduleId}/pay`, data),
};

export const smsAPI = {
  getWallet: () => api.get('/sms/wallet'),
  getPacks: () => api.get('/sms/packs'),
  getPlatformPaymentMethods: () => api.get('/sms/payment-methods'),
  createOrder: (data) => api.post('/sms/orders', data),
  getOrders: () => api.get('/sms/orders'),
  getLogs: (params) => api.get('/sms/logs', { params }),
  sendDirect: (data) => api.post('/sms/send', data),
};

export const agencyExpenseAPI = {
  list: (params) => api.get('/agency-expenses', { params }),
  getById: (id) => api.get(`/agency-expenses/${id}`),
  getStats: (params) => api.get('/agency-expenses/stats', { params }),
   create: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/agency-expenses', data, isFormData ? { headers: { 'Content-Type': undefined } } : {});
  },
  update: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.put(`/agency-expenses/${id}`, data, isFormData ? { headers: { 'Content-Type': undefined } } : {});
  },
  delete: (id) => api.delete(`/agency-expenses/${id}`),
  getCategories: () => api.get('/agency-expenses/categories'),
  createCategory: (data) => api.post('/agency-expenses/categories', data),
};

export const superAdminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAgencies: (params) => api.get('/admin/agencies', { params }),
  getAgencyDetails: (agencyId) => api.get(`/admin/agencies/${agencyId}`),
  toggleAgencyStatus: (agencyId, data) => api.put(`/admin/agencies/${agencyId}/status`, data),
  adjustAgencySms: (agencyId, data) => api.post(`/admin/agencies/${agencyId}/adjust-sms`, data),
  getSmsOrders: (params) => api.get('/admin/sms-orders', { params }),
  processSmsOrder: (orderId, data) => api.put(`/admin/sms-orders/${orderId}/process`, data),
  getSmsLogs: (params) => api.get('/admin/sms-logs', { params }),
  getMasterAccount: () => api.get('/admin/master-account'),
  // ─── Gestion Grilles Tarifaires SMS ──────────────────────────────────────────
  getSmsPackPlans: () => api.get('/admin/sms-pack-plans'),
  createSmsPackPlan: (data) => api.post('/admin/sms-pack-plans', data),
  updateSmsPackPlan: (id, data) => api.put(`/admin/sms-pack-plans/${id}`, data),
  deleteSmsPackPlan: (id) => api.delete(`/admin/sms-pack-plans/${id}`),
  // ─── Gestion Comptes de Paiement Plateforme (Orange Money, Moov Money, Wave) ──
  getPlatformPaymentMethods: () => api.get('/admin/platform-payment-methods'),
  createPlatformPaymentMethod: (data) => api.post('/admin/platform-payment-methods', data),
  updatePlatformPaymentMethod: (id, data) => api.put(`/admin/platform-payment-methods/${id}`, data),
  deletePlatformPaymentMethod: (id) => api.delete(`/admin/platform-payment-methods/${id}`),
};

export default api;

